/**
 * Market tags (#875): port of shared/classify/tools/classify.py. The algorithm is specified in
 * docs/research/CLASSIFICATION.md ("Matching algorithm" and "3b. App behaviour"); the rules ship in the bundle
 * (rules.generated.json, written by shared/classify/tools/gen.py from shared/classify/tags.json). Conformance:
 * shared/classify/fixtures/golden.json + chips.json (classify.test.ts), the same files Android and iOS run.
 * Pure: no React, no I/O.
 */
import { isKeyValueDescription, parseDescription } from '../describe/parse';

export type Network = 'mainnet' | 'testnet';

export interface TagDef {
  id: string;
  parent?: string;
  label: string;
  residual?: boolean;
  fallback?: boolean;
}

interface Condition {
  fields?: string[];
  phrases?: string[];
  equals?: string[];
}

export interface RuleDef extends Condition {
  id: string;
  tags: string[];
  weak?: boolean;
  networks?: string[];
  venues?: string[];
  templates?: string[];
  allOf?: Condition[];
  unless?: Condition;
}

export interface UiConfig {
  hiddenTags: string[];
  multiTopLevel: 'all' | 'first';
  /** How several selected chips combine: 'and' narrows (default), 'or' widens. */
  combine: 'and' | 'or';
}

/** The tags.json file format this build understands. A (downloaded) file with another schemaVersion is rejected. */
export const SUPPORTED_SCHEMA_VERSION = 1;

export interface RulesData {
  schema: string;
  schemaVersion: number;
  version: number;
  updated?: string;
  fieldSets?: Record<string, string[]>;
  tags: TagDef[];
  rules: RuleDef[];
  ui?: Partial<UiConfig>;
}

export interface Rules {
  readonly version: number;
  readonly tags: readonly TagDef[];
  readonly rules: readonly RuleDef[];
  readonly ui: UiConfig;
  /** Tag id -> position in the tree (display order). */
  readonly order: ReadonlyMap<string, number>;
  readonly parent: ReadonlyMap<string, string | undefined>;
  readonly fallback: string;
  readonly residual: readonly string[];
  readonly fieldSets: Readonly<Record<string, string[]>>;
}

/** A source of text: an outcome or a question ({name, description}). */
export interface Source {
  name?: string | null;
  description?: string | null;
}

export interface ClassifyRecord {
  network: Network;
  venue: string;
  families: string[];
  fields: Record<string, string[]>;
}

/** Validating loader (Rules._validate plus the ui block). Throws on a bad file. */
export function loadRules(data: RulesData): Rules {
  if (typeof data !== 'object' || data === null) throw new Error('rules: not an object');
  if (data.schemaVersion !== SUPPORTED_SCHEMA_VERSION) throw new Error(`unsupported schemaVersion ${String(data.schemaVersion)}`);
  if (!Number.isInteger(data.version)) throw new Error('version must be an integer');
  if (!Array.isArray(data.tags) || !Array.isArray(data.rules)) throw new Error('rules: tags and rules must be arrays');
  const order = new Map<string, number>();
  const parent = new Map<string, string | undefined>();
  data.tags.forEach((t, i) => {
    if (order.has(t.id)) throw new Error(`duplicate tag ${t.id}`);
    order.set(t.id, i);
    parent.set(t.id, t.parent);
  });
  for (const t of data.tags) {
    const p = t.parent;
    if (p !== undefined && (!order.has(p) || (order.get(p) ?? 0) > (order.get(t.id) ?? 0))) {
      throw new Error(`tag ${t.id}: parent ${p} must be defined earlier`);
    }
    if (t.residual && !p) throw new Error(`residual tag ${t.id} needs a parent`);
  }
  const fallback = data.tags.filter((t) => t.fallback).map((t) => t.id);
  if (fallback.length !== 1 || parent.get(fallback[0] as string) !== undefined) throw new Error('exactly one top-level fallback tag');
  const fieldSets = data.fieldSets ?? {};
  const ui: UiConfig = {
    hiddenTags: [...(data.ui?.hiddenTags ?? [])],
    multiTopLevel: data.ui?.multiTopLevel ?? 'all',
    combine: data.ui?.combine ?? 'and',
  };
  for (const t of ui.hiddenTags) if (!order.has(t)) throw new Error(`ui.hiddenTags: unknown tag ${t}`);
  if (ui.multiTopLevel !== 'all' && ui.multiTopLevel !== 'first') throw new Error("ui.multiTopLevel must be 'all' or 'first'");
  if (ui.combine !== 'and' && ui.combine !== 'or') throw new Error("ui.combine must be 'and' or 'or'");
  for (const r of data.rules) if (!Array.isArray(r.tags)) throw new Error(`rule ${String(r.id)}: tags must be an array`);
  for (const r of data.rules) {
    for (const tg of r.tags) if (!order.has(tg)) throw new Error(`rule ${r.id}: unknown tag ${tg}`);
    const refs = [...(r.fields ?? []), ...(r.unless?.fields ?? []), ...(r.allOf ?? []).flatMap((c) => c.fields ?? [])];
    for (const f of refs) {
      if (f.startsWith('@') && !Object.prototype.hasOwnProperty.call(fieldSets, f.slice(1))) {
        throw new Error(`rule ${r.id}: unknown field set ${f}`);
      }
    }
  }
  return {
    version: data.version,
    tags: data.tags,
    rules: data.rules,
    ui,
    order,
    parent,
    fallback: fallback[0] as string,
    residual: data.tags.filter((t) => t.residual).map((t) => t.id),
    fieldSets,
  };
}

// ---- record ---------------------------------------------------------------------------------------------------------

/** 'template:binaryPrice6' -> 'binaryPrice'; 'Recurring…' -> 'Recurring'; anything else -> undefined. */
export function familyOfName(name: string | null | undefined): string | undefined {
  if (typeof name !== 'string') return undefined;
  if (name.startsWith('template:')) return name.slice('template:'.length).trim().replace(/[0-9]+$/, '') || undefined;
  if (name.startsWith('Recurring')) return 'Recurring';
  return undefined;
}

function add(fields: Record<string, string[]>, key: string, value: string | undefined) {
  if (value === undefined) return;
  const v = value.trim();
  if (v === '') return;
  (fields[key] ??= []).push(v);
}

/** sources: [question, outcome] (question first; either may be missing). */
export function buildRecord(network: Network, venue: string | null | undefined, sources: readonly (Source | null | undefined)[]): ClassifyRecord {
  const families: string[] = [];
  const fields: Record<string, string[]> = {};
  for (const src of sources) {
    if (!src) continue;
    const name = src.name || '';
    const fam = familyOfName(name);
    if (fam && !families.includes(fam)) families.push(fam);
    if (name && !name.startsWith('template:')) add(fields, '_name', name);
    const desc = src.description || '';
    let pre = desc;
    let post = '';
    const i = desc.indexOf('metadata=');
    if (i >= 0) {
      pre = desc.slice(0, i);
      post = desc.slice(i + 'metadata='.length);
    }
    if (isKeyValueDescription(pre)) {
      for (const [k, v] of Object.entries(parseDescription(pre))) add(fields, k, v);
    } else if (pre.trim() && pre.trim().toLowerCase() !== 'other') {
      add(fields, '_text', pre);
    }
    if (post) for (const [k, v] of Object.entries(parseDescription(post))) add(fields, k, v);
  }
  const ref = (fields.perp ?? fields.underlying ?? [])[0];
  if (ref) {
    const c = ref.indexOf(':');
    add(fields, '_perpDex', c >= 0 ? ref.slice(0, c) : 'main');
    add(fields, '_perpSymbol', c >= 0 ? ref.slice(c + 1) : ref);
  }
  return { network, venue: venue || '', families, fields };
}

export function recordForOutcome(network: Network, outcome: Source & { venue?: string | null }, question?: Source | null): ClassifyRecord {
  return buildRecord(network, outcome.venue, [question ?? {}, outcome]);
}

export function recordForQuestion(network: Network, question: Source, venue?: string | null): ClassifyRecord {
  return buildRecord(network, venue, [question]);
}

// ---- matching -------------------------------------------------------------------------------------------------------

/** NFKD, drop every code point >= U+0080, ASCII lower-case, runs outside [a-z0-9] -> one space, trim. */
export function norm(s: string): string {
  const d = s.normalize('NFKD');
  let ascii = '';
  for (let i = 0; i < d.length; i++) if (d.charCodeAt(i) < 0x80) ascii += d[i];
  return ascii.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function expand(rules: Rules, refs: readonly string[]): string[] {
  const out: string[] = [];
  for (const f of refs) {
    for (const name of f.startsWith('@') ? (rules.fieldSets[f.slice(1)] ?? []) : [f]) if (!out.includes(name)) out.push(name);
  }
  return out;
}

function condHits(rules: Rules, rec: ClassifyRecord, fields: readonly string[], phrases: readonly string[], equals: readonly string[]): boolean {
  const nph = phrases.map((p) => ` ${norm(p)} `);
  const neq = equals.map(norm);
  for (const name of expand(rules, fields)) {
    for (const value of rec.fields[name] ?? []) {
      const nv = norm(value);
      if (neq.includes(nv)) return true;
      const padded = ` ${nv} `;
      for (const p of nph) if (p.trim() && padded.includes(p)) return true;
    }
  }
  return false;
}

export function ruleFires(rules: Rules, r: RuleDef, rec: ClassifyRecord): boolean {
  if (r.networks && !r.networks.includes(rec.network)) return false;
  if (r.venues && !r.venues.includes(rec.venue)) return false;
  if (r.templates && !rec.families.some((f) => r.templates?.includes(f))) return false;
  if (r.phrases || r.equals) {
    if (!condHits(rules, rec, r.fields ?? [], r.phrases ?? [], r.equals ?? [])) return false;
  }
  for (const c of r.allOf ?? []) {
    if (!condHits(rules, rec, c.fields ?? r.fields ?? [], c.phrases ?? [], c.equals ?? [])) return false;
  }
  const u = r.unless;
  if (u && condHits(rules, rec, u.fields ?? r.fields ?? [], u.phrases ?? [], u.equals ?? [])) return false;
  return true;
}

export function rootOf(rules: Rules, tag: string): string {
  let t = tag;
  for (let p = rules.parent.get(t); p; p = rules.parent.get(t)) t = p;
  return t;
}

function withAncestors(rules: Rules, tags: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const tag of tags) for (let t: string | undefined = tag; t; t = rules.parent.get(t)) out.add(t);
  return out;
}

/** The record's tags, in tree order. Never empty (the fallback tag when nothing matched). */
export function classify(rules: Rules, rec: ClassifyRecord): string[] {
  const strong: string[] = [];
  const weak: string[] = [];
  for (const r of rules.rules) if (ruleFires(rules, r, rec)) (r.weak ? weak : strong).push(...r.tags);
  const s = withAncestors(rules, strong);
  const w = withAncestors(rules, weak);
  let result: Set<string>;
  if (s.size > 0) {
    const roots = new Set([...s].map((t) => rootOf(rules, t)));
    result = new Set([...s, ...[...w].filter((t) => roots.has(rootOf(rules, t)))]);
  } else {
    result = w;
  }
  for (const t of rules.residual) {
    const p = rules.parent.get(t);
    if (p && result.has(p) && ![...result].some((x) => rules.parent.get(x) === p)) result.add(t);
  }
  if (result.size === 0) result.add(rules.fallback);
  return [...result].sort((a, b) => (rules.order.get(a) ?? 0) - (rules.order.get(b) ?? 0));
}

// ---- Markets filter chips (classify.py Step 4) ----------------------------------------------------------------------

/** [tag id, number of markets]. */
export type Chip = [string, number];

/** The tags a market is filed under: hidden tags (and descendants) dropped; ui.multiTopLevel 'first' keeps only the
 * first remaining top-level branch. Tree order kept. */
export function filterTags(rules: Rules, tags: readonly string[]): string[] {
  const hidden = new Set(rules.ui.hiddenTags);
  const visible = (tag: string) => {
    for (let t: string | undefined = tag; t; t = rules.parent.get(t)) if (hidden.has(t)) return false;
    return true;
  };
  let out = tags.filter(visible);
  if (rules.ui.multiTopLevel === 'first' && out.length > 0) {
    const first = rootOf(rules, out[0] as string);
    out = out.filter((t) => rootOf(rules, t) === first);
  }
  return out;
}

export function tagCounts(rules: Rules, items: readonly (readonly string[])[]): Map<string, number> {
  const n = new Map<string, number>();
  for (const tags of items) for (const t of filterTags(rules, tags)) n.set(t, (n.get(t) ?? 0) + 1);
  return n;
}

function byCount(rules: Rules, n: ReadonlyMap<string, number>, ids: readonly string[]): Chip[] {
  return [...ids]
    .sort((a, b) => (n.get(b) ?? 0) - (n.get(a) ?? 0) || (rules.order.get(a) ?? 0) - (rules.order.get(b) ?? 0))
    .map((t) => [t, n.get(t) ?? 0]);
}

/** Does a market pass the selected chips? None selected = every market. ui.combine 'and': it has every selected tag;
 * 'or': at least one. */
export function matches(rules: Rules, tags: readonly string[], selected: readonly string[]): boolean {
  if (selected.length === 0) return true;
  const filed = new Set(filterTags(rules, tags));
  return rules.ui.combine === 'or' ? selected.some((t) => filed.has(t)) : selected.every((t) => filed.has(t));
}

/** The remembered chips that still apply, in stored order: known, visible, with markets in view, no duplicates; if
 * together they match no market, none apply (= All). The stored list itself is kept by the caller. */
export function effectiveSelection(rules: Rules, items: readonly (readonly string[])[], stored: readonly string[] | null | undefined): string[] {
  const n = tagCounts(rules, items);
  const out: string[] = [];
  for (const t of stored ?? []) if (rules.order.has(t) && (n.get(t) ?? 0) > 0 && !out.includes(t)) out.push(t);
  if (out.length > 0 && !items.some((tags) => matches(rules, tags, out))) return [];
  return out;
}

export interface ChipRow {
  /** The selected chips in selection order, with their counts. */
  selected: Chip[];
  /** The other chips, most markets first (ties in tree order). */
  offered: Chip[];
  /** Number of markets passing the selection. */
  matching: number;
}

/** The single chip row after "All" (classify.py chip_row): no selection -> top-level tags; 'and' -> every tag in the
 * matching markets that would narrow them further; 'or' -> every other tag in view. An intermediate tag whose single
 * offered child covers all its markets is folded into that child (Basketball 24 = NBA 24 -> NBA). */
export function chipRow(rules: Rules, items: readonly (readonly string[])[], selected: readonly string[]): ChipRow {
  const or = rules.ui.combine === 'or';
  const hit = items.filter((tags) => matches(rules, tags, selected));
  const n = tagCounts(rules, or ? items : hit);
  const all = [...n.keys()];
  let ids: string[];
  if (selected.length === 0) ids = all.filter((t) => rules.parent.get(t) === undefined);
  else if (or) ids = all.filter((t) => !selected.includes(t));
  else ids = all.filter((t) => !selected.includes(t) && (n.get(t) ?? 0) < hit.length);
  const keep = ids.filter((t) => {
    const kids = ids.filter((c) => rules.parent.get(c) === t);
    return !(kids.length === 1 && n.get(kids[0] as string) === n.get(t));
  });
  return { selected: selected.map((t) => [t, n.get(t) ?? 0]), offered: byCount(rules, n, keep), matching: hit.length };
}

/** Card label: the deepest visible sub-tag (first in tree order on a tie); null for top-level-only markets. */
export function cardLabel(rules: Rules, tags: readonly string[]): string | null {
  let best: string | null = null;
  let depth = 0;
  for (const t of filterTags(rules, tags)) {
    let d = 0;
    for (let p = rules.parent.get(t); p; p = rules.parent.get(p)) d++;
    if (d > depth) {
      best = t;
      depth = d;
    }
  }
  return best;
}

/** Catalog key of a tag's display name ('-' -> '_'): world-cup -> tag.world_cup. */
export function tagKey(id: string): string {
  return `tag.${id.replace(/-/g, '_')}`;
}
